
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import ImageUpload from '../components/ImageUpload';

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const VideoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    videoUrl: '',
    thumbnailUrl: '',
    category: 'მატჩის ჰაილაითები',
    duration: '',
    isPublished: true,
    uploadDate: new Date().toISOString().split('T')[0]
  });

  const [youtubePreview, setYoutubePreview] = useState(null);

  const categories = ['მატჩის ჰაილაითები', 'ინტერვიუები', 'ტრენინგი', 'ღონისძიებები', 'სკილები', 'ფანების ვიდეო'];

  // YouTube URL validation and ID extraction
  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const isYouTubeUrl = (url) => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  const generateYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const generateYouTubeEmbedUrl = (videoId) => {
    return `https://www.youtube.com/embed/${videoId}`;
  };


  useEffect(() => {
    if (isEdit) {
      loadVideo();
    }
  }, [id, isEdit]);

  const loadVideo = async () => {
    try {
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        return;
      }
      
      const response = await fetch(getApiUrl(`/api/videos/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const video = await response.json();
        setFormData({
          title: video.title,
          description: video.description || '',
          videoUrl: video.videoUrl,
          thumbnailUrl: video.thumbnailUrl || '',
          category: video.category,
          duration: video.duration,
          isPublished: video.isPublished,
          uploadDate: video.uploadDate ? video.uploadDate.split('T')[0] : new Date().toISOString().split('T')[0]
        });
      } else {
        console.error('Error loading video:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading video:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'videoUrl' && isYouTubeUrl(value)) {
      const videoId = extractYouTubeId(value);
      if (videoId) {
        setYoutubePreview({
          videoId,
          thumbnail: generateYouTubeThumbnail(videoId),
          embedUrl: generateYouTubeEmbedUrl(videoId)
        });
        
        // Auto-fill thumbnail if not already set
        if (!formData.thumbnailUrl) {
          setFormData(prev => ({
            ...prev,
            [name]: value,
            thumbnailUrl: generateYouTubeThumbnail(videoId)
          }));
          return;
        }
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        setLoading(false);
        return;
      }
      
      const url = isEdit 
        ? getApiUrl(`/api/videos/${id}`)
        : getApiUrl('/api/videos');
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        navigate('/_panel-7c39f8/videos');
      } else {
        const error = await response.json();
        console.error('Error saving video:', error);
        alert('შეცდომა ვიდეოს შენახვისას');
      }
    } catch (error) {
      console.error('Error saving video:', error);
      alert('შეცდომა ვიდეოს შენახვისას');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader 
        title={isEdit ? 'ვიდეოს რედაქტირება' : 'ახალი ვიდეო'}
        subtitle={isEdit ? 'შეცვალეთ ვიდეოს ინფორმაცია' : 'დაამატეთ ახალი ვიდეო გალერეაში'}
      >
        <button
          onClick={() => navigate('/_panel-7c39f8/videos')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          უკან
        </button>
      </AdminHeader>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* YouTube Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-blue-900 font-georgian mb-2">
            YouTube ვიდეოების დამატება
          </h3>
          <div className="text-sm text-blue-800 font-georgian space-y-2">
            <p><strong>1.</strong> YouTube-ზე გადახვიდით და აირჩიეთ სასურველი ვიდეო</p>
            <p><strong>2.</strong> "გაზიარება" ღილაკზე დააჭირეთ და URL-ს კოპირება</p>
            <p><strong>3.</strong> ქვემოთ მოცემულ ველში ჩასვით URL (მაგ: https://www.youtube.com/watch?v=VIDEO_ID)</p>
            <p><strong>4.</strong> მინიატურა ავტომატურად გენერირებული იქნება YouTube-დან</p>
            <p><strong>5.</strong> შეავსეთ სათაური, აღწერა, ხანგრძლივობა და სხვა ინფორმაცია</p>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 font-georgian">
                    სათაური *
                  </label>
                  <input
                    type="text"
                    name="title"
                    id="title"
                    required
                    value={formData.title}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="შეიყვანეთ ვიდეოს სათაური"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 font-georgian">
                    აღწერა
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="შეიყვანეთ ვიდეოს აღწერა"
                  />
                </div>

                <div>
                  <label htmlFor="videoUrl" className="block text-sm font-medium text-gray-700 font-georgian">
                    ვიდეოს URL *
                  </label>
                  <input
                    type="url"
                    name="videoUrl"
                    id="videoUrl"
                    required
                    value={formData.videoUrl}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="https://www.youtube.com/watch?v=... ან https://youtu.be/..."
                  />
                  <p className="mt-1 text-sm text-gray-500 font-georgian">
                    YouTube ვიდეოებისთვის: https://www.youtube.com/watch?v=VIDEO_ID ან https://youtu.be/VIDEO_ID
                  </p>
                  
                  {/* YouTube Preview */}
                  {youtubePreview && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 font-georgian mb-2">YouTube ვიდეოს გადახედვა:</h4>
                      <div className="flex items-center space-x-4">
                        <img
                          src={youtubePreview.thumbnail}
                          alt="YouTube thumbnail"
                          className="w-24 h-18 object-cover rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 font-georgian">
                            <strong>Video ID:</strong> {youtubePreview.videoId}
                          </p>
                          <p className="text-xs text-gray-500 font-georgian">
                            მინიატურა ავტომატურად იქნება გენერირებული YouTube-დან
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <ImageUpload
                  value={formData.thumbnailUrl}
                  onChange={(url) => setFormData(prev => ({ ...prev, thumbnailUrl: url }))}
                  category="videos"
                  label="მინიატურა"
                  required={false}
                />

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 font-georgian">
                    კატეგორია *
                  </label>
                  <select
                    name="category"
                    id="category"
                    required
                    value={formData.category}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="duration" className="block text-sm font-medium text-gray-700 font-georgian">
                    ხანგრძლივობა *
                  </label>
                  <input
                    type="text"
                    name="duration"
                    id="duration"
                    required
                    value={formData.duration}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="MM:SS ან HH:MM:SS (მაგ: 5:30)"
                  />
                </div>


                <div>
                  <label htmlFor="uploadDate" className="block text-sm font-medium text-gray-700 font-georgian">
                    ატვირთვის თარიღი
                  </label>
                  <input
                    type="date"
                    name="uploadDate"
                    id="uploadDate"
                    value={formData.uploadDate}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    id="isPublished"
                    name="isPublished"
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={handleChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-900 font-georgian">
                    გამოქვეყნებული
                  </label>
                </div>

              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/_panel-7c39f8/videos')}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
              >
                გაუქმება
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 font-georgian"
              >
                {loading ? 'ინახება...' : (isEdit ? 'განახლება' : 'შენახვა')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VideoForm;
